// Version: 3.0.0 - Landing page + app route split
import React, { Suspense, useState, useEffect } from 'react';
import { lazyWithReload } from './services/lazyWithReload';
import ReactDOM from 'react-dom/client';
import * as Sentry from '@sentry/react';
import './index.css';
import { AppToastProvider } from './components/AppToast';

// ─── Sentry init ─────────────────────────────────────────────────────────
// Minimal error tracking. Runs BEFORE React mounts so early crashes are
// captured. If VITE_SENTRY_DSN is unset (local dev, CI without secrets)
// Sentry noops — no errors, no network calls. Sample rates are conservative
// per the launch brief: 100% error capture, 10% trace sample. No session
// replay, no perf beyond default traces.
//
// PII hard rules: no user email, no prompt content, no image data URLs.
// user.id is the Firebase UID only; that's the minimum needed to correlate
// an error report to a support ticket without exposing the person behind it.
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
if (SENTRY_DSN) {
  // Diagnostic helper — call `window.__skettraTestSentry()` from the
  // browser console after setting VITE_SENTRY_DSN to confirm errors
  // land in Sentry. Also logs the captured event id so you can jump
  // straight to it in the Sentry dashboard.
  if (typeof window !== 'undefined') {
    (window as any).__skettraTestSentry = () => {
      const id = Sentry.captureException(new Error('Sentry test error from Skettra web client'));
      console.log(`[skettra] Sentry test event captured: ${id}`);
      return id;
    };
  }
  Sentry.init({
    dsn: SENTRY_DSN,
    release: import.meta.env.VITE_COMMIT_SHA ?? undefined,
    environment: import.meta.env.MODE,
    sampleRate: 1.0,
    tracesSampleRate: 0.1,
    // Default integrations only. Explicit list keeps out anything that
    // grabs replay, session, or heavy perf — Sentry v10 defaults are
    // already lean but this documents intent.
    integrations: [Sentry.browserTracingIntegration()],
    beforeSend(event) {
      // Scrub any user-shaped payload down to uid.
      if (event.user) {
        event.user = event.user.id ? { id: event.user.id } : {};
      }
      // Strip request bodies — the generation endpoint payloads carry
      // prompts, image data URLs, and reference bytes we never want off-box.
      if (event.request) {
        delete event.request.data;
        delete event.request.cookies;
        if (event.request.headers) {
          delete event.request.headers['authorization'];
          delete event.request.headers['cookie'];
        }
      }
      // Scrub any accidentally-captured email from the error message.
      // Not exhaustive, but catches the common "user martin@x.com failed to X"
      // shape without needing a full PII library.
      if (event.message) {
        event.message = event.message.replace(/[\w.+-]+@[\w-]+\.[\w.-]+/g, '<email>');
      }
      if (event.exception?.values) {
        for (const ex of event.exception.values) {
          if (ex.value) {
            ex.value = ex.value.replace(/[\w.+-]+@[\w-]+\.[\w.-]+/g, '<email>');
          }
        }
      }
      return event;
    },
    beforeBreadcrumb(bc) {
      // Breadcrumbs auto-capture console + fetch. Drop the fetch body
      // for the same reason we drop request.data above.
      if (bc.category === 'fetch' && bc.data) {
        delete (bc.data as Record<string, unknown>).body;
      }
      return bc;
    },
  });
}

// Build marker — set before any UI mounts so it's available to Safari Web
// Inspector on iPad via `window.__SKETTRA_BUILD__` + visible in the corner
// stamp. The console.log survives production because vite.config.ts keeps
// console calls in the bundle (esbuild.drop list no longer includes 'console').
const BUILD_SHA = import.meta.env.VITE_COMMIT_SHA ?? 'unknown';
const BUILD_TIME = import.meta.env.VITE_BUILD_TIME ?? 'unknown';
if (typeof window !== 'undefined') {
  window.__SKETTRA_BUILD__ = { sha: BUILD_SHA, time: BUILD_TIME };
}
// eslint-disable-next-line no-console
console.log(`[skettra] build ${BUILD_SHA} (${BUILD_TIME})`);

/** Glanceable build badge — bottom-right corner.
 *  Hover shows full build time. Click copies the short SHA so Martin can paste
 *  it into chat / commit references when reporting issues. Faint when idle so
 *  it doesn't compete with the UI; brightens on hover. */
const BuildStamp: React.FC = () => {
  const [copied, setCopied] = useState(false);
  const [hovered, setHovered] = useState(false);

  const formatBuildTime = (iso: string) => {
    if (iso === 'unknown') return 'unknown';
    try {
      return new Date(iso).toLocaleString(undefined, {
        year: 'numeric', month: 'short', day: 'numeric',
        hour: '2-digit', minute: '2-digit',
      });
    } catch { return iso; }
  };

  const handleClick = async () => {
    try {
      await navigator.clipboard.writeText(BUILD_SHA);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // navigator.clipboard fails in non-secure contexts; ignore silently.
    }
  };

  const isElevated = hovered || copied;

  return (
    <div
      onClick={handleClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      title={`Build ${BUILD_SHA} · ${formatBuildTime(BUILD_TIME)}\nClick to copy SHA`}
      style={{
        position: 'fixed',
        // Bottom-left: bottom-right is busy across the app (gallery hover info,
        // toast stacks, viewer chrome). Bottom-left is reliably empty.
        bottom: 6,
        left: 8,
        fontSize: 10,
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
        color: isElevated ? 'rgba(226, 232, 240, 1)' : 'rgba(203, 213, 225, 0.85)',
        background: isElevated ? 'rgba(15, 23, 42, 0.95)' : 'rgba(15, 23, 42, 0.75)',
        border: `1px solid ${isElevated ? 'rgba(148, 163, 184, 0.5)' : 'rgba(148, 163, 184, 0.25)'}`,
        padding: '2px 6px',
        borderRadius: 4,
        cursor: 'pointer',
        // Sit above EnhancePanel (z-61) and any other overlay we ship.
        zIndex: 9999,
        userSelect: 'none',
        transition: 'color 120ms, background 120ms, border-color 120ms',
        backdropFilter: 'blur(4px)',
        WebkitBackdropFilter: 'blur(4px)',
        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.3)',
      }}
    >
      {copied ? '✓ copied' : `build ${BUILD_SHA}`}
      {hovered && !copied && (
        <span style={{ marginLeft: 6, opacity: 0.7 }}>
          · {formatBuildTime(BUILD_TIME)}
        </span>
      )}
    </div>
  );
};

// Lazy-load both entry points for code splitting
const App = lazyWithReload(() => import('./App'));
const LandingPage = lazyWithReload(() => import('./components/LandingPage'));
const DiagnosticsHUD = lazyWithReload(() => import('./components/DiagnosticsHUD').then(m => ({ default: m.DiagnosticsHUD })));
const DiagnosticsProvenancePanel = lazyWithReload(() => import('./components/DiagnosticsProvenancePanel').then(m => ({ default: m.DiagnosticsProvenancePanel })));

// Re-read the URL param once at module load — Vite HMR reloads this file when
// changed, and we don't need to re-check per-render.
const diagEnabled = typeof window !== 'undefined'
  && new URLSearchParams(window.location.search).has('diag');

/** Suspense fallback. Matches the inline splash in index.html so the
 *  transition from "HTML loaded, JS loading" → "React mounted, chunk
 *  loading" doesn't visibly flicker — same wordmark, same accent ring. */
const Loader = () => (
  <div className="min-h-screen flex flex-col items-center justify-center" style={{ background: '#0B0D12', color: '#B2CDE1' }}>
    <div className="font-semibold tracking-[0.08em]" style={{ fontSize: 28, marginBottom: 18 }}>SKETTRA</div>
    <div className="w-7 h-7 border-2 rounded-full animate-spin" style={{ borderColor: 'rgba(106,157,189,0.25)', borderTopColor: '#6A9DBD' }} />
  </div>
);

/** Global offline banner. Listens for the browser's online/offline events
 *  and shows a thin strip at the top of the viewport when the user goes
 *  offline. Canvas drawing keeps working (it's local) — this just sets
 *  expectations about AI / cloud sync until the connection is back. */
const OfflineBanner: React.FC = () => {
  const [offline, setOffline] = useState(typeof navigator !== 'undefined' && !navigator.onLine);
  useEffect(() => {
    const goOnline = () => setOffline(false);
    const goOffline = () => setOffline(true);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);
  if (!offline) return null;
  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed', top: 0, left: 0, right: 0,
        zIndex: 99999,
        background: '#7C2D12',
        color: '#FED7AA',
        textAlign: 'center',
        padding: '6px 12px',
        fontSize: 12,
        fontFamily: 'Inter, system-ui, sans-serif',
        fontWeight: 500,
        letterSpacing: 0.2,
        borderBottom: '1px solid rgba(255,255,255,0.08)',
        paddingTop: 'max(6px, env(safe-area-inset-top))',
      }}
    >
      You're offline — drawing still works, but AI generation, cloud sync, and the gallery will resume when you're back online.
    </div>
  );
};

/** Lightweight path-based router — no library needed for two routes */
const Router: React.FC = () => {
  const path = window.location.pathname;
  const isAppRoute = path === '/app' || path.startsWith('/app/');

  return (
    <Suspense fallback={<Loader />}>
      {isAppRoute ? (
        <AppToastProvider>
          <App />
        </AppToastProvider>
      ) : (
        <LandingPage />
      )}
      <BuildStamp />
      <OfflineBanner />
      {diagEnabled && <DiagnosticsHUD />}
      {diagEnabled && <DiagnosticsProvenancePanel />}
    </Suspense>
  );
};

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error("Could not find root element to mount to");
}

const root = ReactDOM.createRoot(rootElement);
root.render(
  <React.StrictMode>
    <Router />
  </React.StrictMode>
);
